/**
 * Screen printing (Serigrafi Baskı / Screen Printing / Siebdruck): page copy + SEO.
 * Every string is VERBATIM from the pre-redesign Serigrafi.jsx,
 * en/ScreenPrinting.jsx and de/Siebdruck.jsx. The `seo` blocks reproduce the
 * exact head writes each page made (TR: keywords, OG, Twitter, Service JSON-LD;
 * EN and DE: title and description only; the canonical is CanonicalManager's
 * now). Key order inside `schema` is load-bearing (byte-identical
 * JSON.stringify).
 */

const screen = {
  tr: {
    seo: {
      title: 'Serigrafi Baskı Hizmetleri | Bursa Serigrafi - Elek Baskı Firması - Ankaplastik',
      description:
        'Bursa serigrafi baskı (screen printing) hizmetleri. Elek baskı teknolojisi ile kombi panelleri, gösterge panelleri, endüstriyel baskı. Serigrafi baskı fiyatları için teklif alın. 25 yıllık deneyim.',
      keywords:
        'serigrafi baskı, bursa serigrafi, elek baskı, screen printing, serigrafi baskı fiyatları, serigrafi baskı yapan firmalar, ipek baskı, endüstriyel serigrafi',
      og: {
        'og:title': 'Serigrafi Baskı Hizmetleri | Ankaplastik Bursa',
        'og:description':
          "Bursa'da profesyonel serigrafi baskı (screen printing) hizmetleri. Geniş yüzeylerde yüksek kaliteli endüstriyel baskı çözümleri.",
        'og:type': 'website',
        'og:url': 'https://ankaplastik.com/serigrafi',
        'og:image': 'https://ankaplastik.com/og-serigrafi.jpg',
        'og:image:width': '1200',
        'og:image:height': '630',
        'og:locale': 'tr_TR',
        'og:site_name': 'Ankaplastik',
      },
      twitter: {
        'twitter:card': 'summary_large_image',
        'twitter:title': 'Serigrafi Baskı Hizmetleri | Ankaplastik',
        'twitter:description': "Bursa'da profesyonel serigrafi baskı hizmetleri. Geniş yüzeylerde endüstriyel baskı.",
        'twitter:image': 'https://ankaplastik.com/og-serigrafi.jpg',
      },
      schemaId: 'serigrafi',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Serigrafi Baskı Hizmetleri',
        alternateName: ['Screen Printing', 'Elek Baskı', 'İpek Baskı'],
        provider: {
          '@type': 'LocalBusiness',
          '@id': 'https://ankaplastik.com/#organization',
          name: 'Ankaplastik',
        },
        serviceType: 'Endüstriyel Baskı',
        areaServed: {
          '@type': 'Country',
          name: 'Türkiye',
        },
        description:
          'Geniş yüzeylerde yüksek kaliteli serigrafi baskı (screen printing) hizmetleri. Kombi panelleri, gösterge panelleri, endüstriyel kontrol panelleri için baskı çözümleri.',
        offers: {
          '@type': 'Offer',
          availability: 'https://schema.org/InStock',
          priceSpecification: {
            '@type': 'PriceSpecification',
            priceCurrency: 'TRY',
          },
        },
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Serigrafi Baskı Uygulamaları',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kombi ve Beyaz Eşya Panel Baskısı' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Otomotiv Gösterge Paneli Baskısı' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Endüstriyel Kontrol Paneli Baskısı' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Cam ve Metal Yüzey Baskısı' } },
          ],
        },
      },
    },
    hero: {
      titleLead: 'Serigrafi Baskı',
      titleTail: 'Hizmetleri',
      lead: 'Düz ve geniş yüzeylerde yüksek kaliteli baskı için serigrafi teknolojimizle, dayanıklı ve canlı renkli sonuçlar elde ediyoruz.',
    },
    intro: {
      title: 'Elek Baskı Teknolojisi',
      paragraphs: [
        'Serigrafi (screen printing), ince bir elek üzerinden mürekkebin baskı yapılacak yüzeye aktarıldığı geleneksel ve güvenilir bir baskı yöntemidir.',
        'Özellikle düz ve geniş yüzeylerde yüksek mürekkep kalınlığı gerektiren uygulamalarda tercih edilir. Kombi panelleri, gösterge panelleri ve endüstriyel işaretlemeler için ideal bir çözümdür.',
      ],
    },
    advantages: [
      { title: 'Geniş Baskı Alanı', desc: 'Büyük yüzeylerde tek seferde kaliteli baskı imkanı' },
      { title: 'Yüksek Renk Canlılığı', desc: 'Parlak ve net renklerle dikkat çekici baskılar' },
      { title: 'Yüksek Dayanıklılık', desc: 'UV ve kimyasallara karşı uzun ömürlü baskılar' },
      { title: 'Çeşitli Malzemeler', desc: 'Plastik, cam, metal ve tekstil üzerine baskı' },
      { title: 'Hızlı Üretim', desc: 'Yüksek adetli siparişler için verimli prosesler' },
      { title: 'Kalıcı Sonuçlar', desc: 'Uzun yıllar solmayan, aşınmayan baskılar' },
    ],
    applications: [
      'Kombi ve beyaz eşya ön panelleri',
      'Otomotiv gösterge panelleri',
      'Endüstriyel kontrol panelleri',
      'Elektronik cihaz kaplamaları',
      'Cam ve ayna baskıları',
      'Dekoratif yüzey uygulamaları',
    ],
    stats: [
      { label: 'Deneyim', value: '25+ yıl' },
      { label: 'Hata Oranı', value: '%0.01' },
    ],
    contact: {
      description: 'Serigrafi baskı ihtiyaçlarınız için bizimle iletişime geçin.',
      subjectPrefix: 'Serigrafi',
    },
    related: [
      {
        service: 'pad',
        name: 'Tampon Baskı',
        desc: '3D yüzeylere mükemmel uyum sağlayan pad printing teknolojisi sunuyoruz.',
        features: ['Mikron hassasiyetinde baskı', 'Çok renkli uygulamalar'],
      },
      {
        service: 'assembly',
        name: 'Montaj & Trim',
        desc: 'Otomotiv trim parçaları ve güvenlik bileşenlerinin hassas montajını gerçekleştiriyoruz.',
        features: ['Hassas parça montajı', 'ISOFIX güvenlik parçaları'],
      },
    ],
  },

  en: {
    seo: {
      title: 'Screen Printing Services | Industrial Screen Printing - Ankaplastik Turkey',
      description:
        'Professional screen printing services for large surfaces. High ink deposit, vibrant colors, UV-resistant. Ideal for automotive panels, appliance controls, industrial equipment.',
    },
    hero: {
      titleLead: 'Screen Printing',
      titleTail: 'Services',
      lead: 'High-quality screen printing technology for flat and large surfaces, delivering durable results with vibrant, lasting colors.',
    },
    intro: {
      title: 'Mesh Printing Technology',
      paragraphs: [
        'Screen printing (serigraphy) is a technique where ink is pushed through a fine mesh stencil onto the substrate, creating durable and vibrant prints.',
        "This method is ideal for large, flat surfaces requiring thick ink deposits. It's extensively used for control panels, appliance interfaces, and automotive dashboards.",
      ],
    },
    advantages: [
      { title: 'Large Print Area', desc: 'Single-pass quality printing on large surfaces' },
      { title: 'Vibrant Colors', desc: 'Eye-catching prints with bright, sharp colors' },
      { title: 'High Durability', desc: 'Long-lasting prints resistant to UV and chemicals' },
      { title: 'Material Versatility', desc: 'Print on plastic, glass, metal, and textiles' },
      { title: 'Fast Production', desc: 'Efficient processes for high-volume orders' },
      { title: 'Lasting Results', desc: 'Prints that resist fading and wear for years' },
    ],
    applications: [
      'Boiler and appliance front panels',
      'Automotive dashboard displays',
      'Industrial control panels',
      'Electronic device housings',
      'Glass and mirror printing',
      'Decorative surface applications',
    ],
    stats: [
      { label: 'Experience', value: '25+ years' },
      { label: 'Defect Rate', value: '0.01%' },
    ],
    contact: {
      description: 'Contact us for your screen printing requirements.',
      subjectPrefix: 'Screen Printing',
    },
    related: [
      {
        service: 'pad',
        name: 'Pad Printing',
        desc: 'Precision pad printing for complex 3D surfaces and irregular geometries.',
        features: ['Multi-color printing', 'Micron-level precision'],
      },
      {
        service: 'assembly',
        name: 'Assembly & Trim',
        desc: 'Precision assembly of automotive trim parts and safety components.',
        features: ['Precision component assembly', 'ISOFIX safety parts'],
      },
    ],
  },

  de: {
    seo: {
      title: 'Siebdruck-Service | Industrieller Siebdruck - Ankaplastik Türkei',
      description:
        'Professioneller Siebdruck für große Oberflächen. Hoher Farbauftrag, lebendige Farben, UV-beständig. Ideal für Fahrzeugpaneele, Gerätesteuerungen, Industrieanlagen.',
    },
    hero: {
      titleLead: 'Siebdruck',
      titleTail: 'Services',
      lead: 'Hochwertige Siebdrucktechnologie für flache und große Oberflächen, die langlebige Ergebnisse mit lebendigen, dauerhaften Farben liefert.',
    },
    intro: {
      title: 'Netzdrucktechnologie',
      paragraphs: [
        'Siebdruck (Serigrafie) ist eine Technik, bei der Farbe durch ein feines Netz auf das Substrat gedrückt wird, wodurch langlebige und lebendige Drucke entstehen.',
        'Diese Methode ist ideal für große, flache Oberflächen, die einen dicken Farbauftrag erfordern. Sie wird häufig für Bedienfelder, Gerätefronten und Fahrzeugarmaturenbretter verwendet.',
      ],
    },
    advantages: [
      { title: 'Großer Druckbereich', desc: 'Qualitätsdruck in einem Durchgang auf großen Oberflächen' },
      { title: 'Lebendige Farben', desc: 'Auffällige Drucke mit hellen, scharfen Farben' },
      { title: 'Hohe Haltbarkeit', desc: 'Langlebige Drucke, beständig gegen UV und Chemikalien' },
      { title: 'Materialvielseitigkeit', desc: 'Druck auf Kunststoff, Glas, Metall und Textilien' },
      { title: 'Schnelle Produktion', desc: 'Effiziente Prozesse für Großserienaufträge' },
      { title: 'Dauerhafte Ergebnisse', desc: 'Drucke, die jahrelang gegen Verblassen und Verschleiß beständig sind' },
    ],
    applications: [
      'Frontplatten für Kessel und Haushaltsgeräte',
      'Fahrzeug-Armaturenbrettanzeigen',
      'Industrielle Bedienfelder',
      'Gehäuse für elektronische Geräte',
      'Glas- und Spiegeldruck',
      'Dekorative Oberflächenanwendungen',
    ],
    stats: [
      { label: 'Erfahrung', value: '25+ Jahre' },
      { label: 'Fehlerquote', value: '0.01%' },
    ],
    contact: {
      description: 'Kontaktieren Sie uns für Ihre Siebdruck-Anforderungen.',
      subjectPrefix: 'Siebdruck',
    },
    related: [
      {
        service: 'pad',
        name: 'Tampondruck',
        desc: 'Präzisions-Tampondruck für komplexe 3D-Oberflächen und unregelmäßige Geometrien.',
        features: ['Mehrfarbendruck', 'Mikrometergenaue Präzision'],
      },
      {
        service: 'assembly',
        name: 'Montage & Trimteile',
        desc: 'Präzisionsmontage von Fahrzeug-Trimteilen und Sicherheitskomponenten.',
        features: ['Präzisionsmontage von Komponenten', 'ISOFIX-Sicherheitsteile'],
      },
    ],
  },
}

export default screen
